import { defineStore } from 'pinia'
import { nextTick } from 'vue'
import {
  buildBlockDefaults,
  buildPageDefaults,
  DEFAULT_FONT_FAMILY,
  DEFAULT_THEME_NEUTRAL,
  DEFAULT_THEME_PRIMARY,
  getLocaleInfo,
  normalizeThemeNeutral,
  normalizeThemePrimary,
  primaryFromLegacyHex,
  type Locale,
} from '~/composables/useBuilderMeta'
import {
  isLayoutFooter,
  isLayoutHeader,
  normalizeLayoutBlockOrder,
} from '~/utils/layoutChrome'

type Translatable = Record<string, string>

export type Block = {
  component: string
  enabled?: boolean
  props: Record<string, any>
}

export type PageConfig = {
  title: Translatable
  slug: string
  enabled?: boolean
  blocks: Block[]
  description?: Translatable
  seoTitle?: Translatable
  seoDescription?: Translatable
  seoImage?: string
  noindex?: boolean
}

export type SiteData = {
  i18n: {
    enabled: boolean
    locales: Locale[]
    defaultLocale: Locale
  }
  title: Translatable
  siteSettings: {
    themePrimary: string
    themeNeutral: string
    fontFamily: string
    logoUrl?: string
    favicon?: string
    description?: Translatable
  }
  layout: {
    blocks: Block[]
  }
  pages: Record<string, PageConfig>
}

type DeviceMode = 'desktop' | 'mobile'
type InteractionMode = 'browse' | 'edit'
type SidebarSection = 'global' | 'pages' | 'blocks' | 'languages'
export type SheetSnap = 'peek' | 'half' | 'full'

interface BuilderState {
  siteData: SiteData | null
  activePageSlug: string
  selectedBlockIndex: number | null
  /** When true, the selected block lives in the layout (header/footer) instead of the page list. */
  selectedScope: 'page' | 'layout'
  deviceMode: DeviceMode
  interactionMode: InteractionMode
  sidebarSection: SidebarSection
  editLocale: Locale
  isDirty: boolean
  sheetSnap: SheetSnap
}

function ensureLocale<T extends Translatable | undefined>(
  value: T,
  locale: Locale,
  fallback: string,
): Translatable {
  const safe: Translatable = value && typeof value === 'object' ? { ...value } : {}
  if (!safe[locale]) safe[locale] = fallback
  return safe
}

/** Recursively make sure every translatable object on a value has the given locale filled in. */
function fillLocaleDeep(value: any, locale: Locale): void {
  if (!value || typeof value !== 'object') return
  if (Array.isArray(value)) {
    value.forEach((item) => fillLocaleDeep(item, locale))
    return
  }
  // Heuristic: an object with all string values is treated as a translatable map
  const entries = Object.entries(value)
  const isStringMap =
    entries.length > 0 && entries.every(([, v]) => typeof v === 'string')
  if (isStringMap) {
    if (value[locale] == null || value[locale] === '') {
      const fallback = (Object.values(value)[0] as string) ?? ''
      value[locale] = fallback
    }
    return
  }
  Object.values(value).forEach((v) => fillLocaleDeep(v, locale))
}

function removeLocaleDeep(value: any, locale: Locale): void {
  if (!value || typeof value !== 'object') return
  if (Array.isArray(value)) {
    value.forEach((item) => removeLocaleDeep(item, locale))
    return
  }
  const entries = Object.entries(value)
  const isStringMap =
    entries.length > 0 && entries.every(([, v]) => typeof v === 'string')
  if (isStringMap) {
    delete (value as Record<string, string>)[locale]
    return
  }
  Object.values(value).forEach((v) => removeLocaleDeep(v, locale))
}

function normalizeSiteData(siteData: SiteData): SiteData {
  if (!siteData.siteSettings) {
    siteData.siteSettings = {
      themePrimary: DEFAULT_THEME_PRIMARY,
      themeNeutral: DEFAULT_THEME_NEUTRAL,
      fontFamily: DEFAULT_FONT_FAMILY,
    }
  }
  const settings = siteData.siteSettings as SiteData['siteSettings'] & { themeColor?: string }
  if (!settings.themePrimary) {
    settings.themePrimary = primaryFromLegacyHex(settings.themeColor) || DEFAULT_THEME_PRIMARY
  }
  settings.themePrimary = normalizeThemePrimary(settings.themePrimary)
  settings.themeNeutral = normalizeThemeNeutral(settings.themeNeutral)
  delete settings.themeColor
  if (siteData.siteSettings.favicon === undefined) siteData.siteSettings.favicon = ''
  if (siteData.siteSettings.logoUrl === undefined) siteData.siteSettings.logoUrl = ''
  if (!siteData.siteSettings.fontFamily) {
    siteData.siteSettings.fontFamily = DEFAULT_FONT_FAMILY
  }

  if (!siteData.i18n) {
    siteData.i18n = { enabled: true, locales: ['en', 'fa'], defaultLocale: 'en' }
  }
  if (!Array.isArray(siteData.i18n.locales) || siteData.i18n.locales.length === 0) {
    siteData.i18n.locales = ['en']
  }
  if (!siteData.i18n.locales.includes(siteData.i18n.defaultLocale)) {
    siteData.i18n.defaultLocale = siteData.i18n.locales[0] ?? 'en'
  }

  if (!siteData.layout) siteData.layout = { blocks: [] }
  if (!Array.isArray(siteData.layout.blocks)) siteData.layout.blocks = []

  for (const block of siteData.layout.blocks) {
    if (block.enabled === undefined) block.enabled = true
  }
  // Header first, mid chrome (banner…), footer last — always.
  siteData.layout.blocks = normalizeLayoutBlockOrder(siteData.layout.blocks)

  for (const page of Object.values(siteData.pages ?? {})) {
    if (page.enabled === undefined) page.enabled = true
    if (!Array.isArray(page.blocks)) page.blocks = []
    for (const block of page.blocks) {
      if (block.enabled === undefined) block.enabled = true
    }
  }

  return siteData
}

function syncHeaderFromGlobal(siteData: SiteData) {
  const header = siteData.layout.blocks.find((b) => b.component === 'BlockHeader')
  if (!header) return
  header.props.logoImage = siteData.siteSettings.logoUrl ?? header.props.logoImage ?? ''
  header.props.logoText = { ...siteData.title }
}

function syncFooterFromGlobal(siteData: SiteData) {
  const footer = siteData.layout.blocks.find((b) => b.component === 'BlockFooter')
  if (!footer) return
  footer.props.logoText = { ...siteData.title }
}

function uniqueSlug(siteData: SiteData, base: string): string {
  let candidate = base.replace(/[^a-z0-9-]/gi, '-').toLowerCase() || 'page'
  if (!siteData.pages[candidate]) return candidate
  let i = 2
  while (siteData.pages[`${candidate}-${i}`]) i++
  return `${candidate}-${i}`
}

export const useBuilderStore = defineStore('builder', {
  state: (): BuilderState => ({
    siteData: null,
    activePageSlug: 'home',
    selectedBlockIndex: null,
    selectedScope: 'page',
    deviceMode: 'desktop',
    interactionMode: 'browse',
    sidebarSection: 'pages',
    editLocale: 'fa',
    isDirty: false,
    sheetSnap: 'peek',
  }),

  getters: {
    activePage(state): PageConfig | null {
      if (!state.siteData) return null
      return state.siteData.pages[state.activePageSlug] ?? null
    },

    enabledPages(state): Array<{ key: string } & PageConfig> {
      if (!state.siteData) return []
      return Object.entries(state.siteData.pages).map(([key, page]) => ({
        key,
        ...page,
      }))
    },

    activePageBlocks(state): Block[] {
      const page = state.siteData?.pages[state.activePageSlug]
      return page?.blocks ?? []
    },

    layoutBlocks(state): Block[] {
      return state.siteData?.layout.blocks ?? []
    },

    selectedBlock(): Block | null {
      if (this.selectedBlockIndex === null) return null
      const list = this.selectedScope === 'layout' ? this.layoutBlocks : this.activePageBlocks
      return list[this.selectedBlockIndex] ?? null
    },

    isBrowseMode(state): boolean {
      return state.interactionMode === 'browse'
    },

    locales(state): Locale[] {
      return state.siteData?.i18n.locales ?? ['en']
    },

    defaultLocale(state): Locale {
      return state.siteData?.i18n.defaultLocale ?? 'en'
    },
  },

  actions: {
    setSiteData(siteData: SiteData) {
      this.siteData = normalizeSiteData(siteData)
      syncHeaderFromGlobal(this.siteData)
      syncFooterFromGlobal(this.siteData)
      // Prefer the site's default locale for editing / preview
      if (this.siteData.i18n.locales.includes(this.siteData.i18n.defaultLocale)) {
        this.editLocale = this.siteData.i18n.defaultLocale
      } else if (!this.siteData.i18n.locales.includes(this.editLocale)) {
        this.editLocale = this.siteData.i18n.locales[0] ?? 'en'
      }
    },

    markDirty() {
      this.isDirty = true
    },

    markClean() {
      this.isDirty = false
    },

    async save() {
      if (!this.siteData) return { ok: false, error: 'No site data' }
      try {
        const { loggedIn } = useUserSession()
        if (!loggedIn.value) {
          if (import.meta.client) {
            localStorage.setItem('aradcore:site-draft', JSON.stringify(this.siteData))
          }
        return { ok: true, local: true as const }
      }
      await $fetch('/api/me/site', {
        method: 'POST',
        body: { data: this.siteData },
      })
      this.markClean()
      return { ok: true, local: false as const }
      } catch (err: unknown) {
        const message = (err as { data?: { statusMessage?: string } })?.data?.statusMessage
          || (err instanceof Error ? err.message : 'Save failed')
        return { ok: false, error: message }
      }
    },

    /**
     * Apply a SiteSchema produced by the onboarding wizard.
     * Prefer useOnboardingStore.commit() which also persists to localStorage.
     */
    applyOnboardingSchema(schema: SiteData) {
      this.setSiteData(JSON.parse(JSON.stringify(schema)) as SiteData)
      this.activePageSlug = 'home'
      this.selectedBlockIndex = null
      this.selectedScope = 'page'
      this.markDirty()
    },

    /* ───── Selection ──────────────────────────── */

    setActivePageSlug(slug: string) {
      const page = this.siteData?.pages[slug]
      if (!page || page.enabled === false) return
      this.activePageSlug = slug
      this.selectedBlockIndex = null
      this.selectedScope = 'page'
    },

    selectBlock(index: number | null, scope: 'page' | 'layout' = 'page') {
      this.selectedBlockIndex = index
      this.selectedScope = scope
      if (index !== null) this.sidebarSection = 'blocks'
    },

    setDeviceMode(mode: DeviceMode) {
      this.deviceMode = mode
    },

    setInteractionMode(mode: InteractionMode) {
      this.interactionMode = mode
    },

    setSidebarSection(section: SidebarSection) {
      this.sidebarSection = section
    },

    setSheetSnap(snap: SheetSnap) {
      this.sheetSnap = snap
    },

    setEditLocale(locale: Locale) {
      if (!this.siteData) return
      if (!this.siteData.i18n.locales.includes(locale)) return
      this.editLocale = locale
    },

    /* ───── Site settings ──────────────────────── */

    setThemePrimary(color: string) {
      if (!this.siteData) return
      this.siteData.siteSettings.themePrimary = color
      this.markDirty()
    },

    setThemeNeutral(color: string) {
      if (!this.siteData) return
      this.siteData.siteSettings.themeNeutral = color
      this.markDirty()
    },

    setSiteTitle(locale: Locale, value: string) {
      if (!this.siteData) return
      this.siteData.title[locale] = value
      syncHeaderFromGlobal(this.siteData)
      syncFooterFromGlobal(this.siteData)
      this.markDirty()
    },

    setLogoUrl(url: string) {
      if (!this.siteData) return
      this.siteData.siteSettings.logoUrl = url
      syncHeaderFromGlobal(this.siteData)
      this.markDirty()
    },

    setFavicon(url: string) {
      if (!this.siteData) return
      this.siteData.siteSettings.favicon = url
      this.markDirty()
    },

    setFontFamily(family: string) {
      if (!this.siteData) return
      this.siteData.siteSettings.fontFamily = family
      this.markDirty()
    },

    setSiteDescription(locale: Locale, value: string) {
      if (!this.siteData) return
      this.siteData.siteSettings.description = {
        ...this.siteData.siteSettings.description,
        [locale]: value,
      }
      this.markDirty()
    },

    /* ───── Pages ───────────────────────────────── */

    setPageEnabled(pageKey: string, enabled: boolean) {
      if (!this.siteData) return
      const page = this.siteData.pages[pageKey]
      if (!page) return
      page.enabled = enabled
      if (pageKey === this.activePageSlug && !enabled) {
        const fallback = Object.entries(this.siteData.pages).find(
          ([, p]) => p.enabled !== false,
        )
        if (fallback) this.setActivePageSlug(fallback[0])
      }
      this.markDirty()
    },

    setPageTitle(pageKey: string, locale: Locale, value: string) {
      const page = this.siteData?.pages[pageKey]
      if (!page) return
      page.title = { ...page.title, [locale]: value }
      this.markDirty()
    },

    setPageSeoTitle(pageKey: string, locale: Locale, value: string) {
      const page = this.siteData?.pages[pageKey]
      if (!page) return
      page.seoTitle = { ...page.seoTitle, [locale]: value }
      this.markDirty()
    },

    setPageSeoDescription(pageKey: string, locale: Locale, value: string) {
      const page = this.siteData?.pages[pageKey]
      if (!page) return
      page.seoDescription = { ...page.seoDescription, [locale]: value }
      this.markDirty()
    },

    setPageSeoImage(pageKey: string, value: string) {
      const page = this.siteData?.pages[pageKey]
      if (!page) return
      page.seoImage = value
      this.markDirty()
    },

    setPageNoindex(pageKey: string, value: boolean) {
      const page = this.siteData?.pages[pageKey]
      if (!page) return
      page.noindex = value
      this.markDirty()
    },

    setPageSlug(pageKey: string, slug: string) {
      const page = this.siteData?.pages[pageKey]
      if (!page) return
      const cleaned = slug.startsWith('/') ? slug : `/${slug}`
      page.slug = cleaned
      // Update header menu items targeting old slug
      this.markDirty()
    },

    addPage(input: { key?: string; title: Translatable; slug?: string }): string | null {
      if (!this.siteData) return null
      const baseKey = (input.key || input.title.en || input.title[this.editLocale] || 'page')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
      const key = uniqueSlug(this.siteData, baseKey)
      const slug = input.slug ?? `/${key}`
      this.siteData.pages[key] = {
        title: { ...input.title },
        slug,
        enabled: true,
        ...buildPageDefaults(),
      }
      // Make sure title has all locales filled in
      const newPage = this.siteData.pages[key]
      if (newPage) {
        for (const loc of this.siteData.i18n.locales) {
          if (!newPage.title[loc]) {
            newPage.title[loc] = input.title[loc] ?? input.title[this.editLocale] ?? key
          }
        }
      }
      this.activePageSlug = key
      this.selectedBlockIndex = null
      this.selectedScope = 'page'
      this.sidebarSection = 'blocks'
      this.markDirty()
      return key
    },

    removePage(pageKey: string) {
      if (!this.siteData) return
      if (pageKey === 'home') return // protect home
      delete this.siteData.pages[pageKey]
      if (this.activePageSlug === pageKey) {
        const next = Object.keys(this.siteData.pages)[0] ?? 'home'
        this.activePageSlug = next
        this.selectedBlockIndex = null
      }
      this.markDirty()
    },

    /* ───── Blocks ──────────────────────────────── */

    setBlockEnabled(index: number, enabled: boolean, scope: 'page' | 'layout' = 'page') {
      const list = scope === 'layout'
        ? this.siteData?.layout.blocks
        : this.siteData?.pages[this.activePageSlug]?.blocks
      const block = list?.[index]
      if (!block) return
      block.enabled = enabled
      this.markDirty()
    },

    removeBlock(index: number, scope: 'page' | 'layout' = 'page') {
      const list = scope === 'layout'
        ? this.siteData?.layout.blocks
        : this.siteData?.pages[this.activePageSlug]?.blocks
      if (!list) return
      list.splice(index, 1)
      if (
        this.selectedScope === scope &&
        this.selectedBlockIndex !== null
      ) {
        if (this.selectedBlockIndex === index) {
          this.selectedBlockIndex = null
        } else if (this.selectedBlockIndex > index) {
          this.selectedBlockIndex -= 1
        }
      }
      this.markDirty()
    },

    moveBlock(index: number, direction: -1 | 1, scope: 'page' | 'layout' = 'page') {
      const list = scope === 'layout'
        ? this.siteData?.layout.blocks
        : this.siteData?.pages[this.activePageSlug]?.blocks
      if (!list) return
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= list.length) return

      if (scope === 'layout') {
        const moving = list[index]
        const target = list[newIndex]
        if (!moving || !target) return
        // Header stays first; footer stays last.
        if (isLayoutHeader(moving.component) && direction === -1) return
        if (isLayoutFooter(moving.component) && direction === 1) return
        if (isLayoutHeader(target.component) && direction === -1) return
        if (isLayoutFooter(target.component) && direction === 1) return
      }

      const [item] = list.splice(index, 1)
      if (!item) return
      list.splice(newIndex, 0, item)
      if (scope === 'layout') {
        this.siteData!.layout.blocks = normalizeLayoutBlockOrder(list)
        const normalizedIndex = this.siteData!.layout.blocks.findIndex((b) => b === item)
        if (this.selectedScope === 'layout' && this.selectedBlockIndex === index) {
          this.selectedBlockIndex = normalizedIndex >= 0 ? normalizedIndex : newIndex
        }
      } else if (this.selectedScope === scope && this.selectedBlockIndex === index) {
        this.selectedBlockIndex = newIndex
      }
      this.markDirty()
    },

    duplicateBlock(index: number, scope: 'page' | 'layout' = 'page') {
      const list = scope === 'layout'
        ? this.siteData?.layout.blocks
        : this.siteData?.pages[this.activePageSlug]?.blocks
      if (!list) return
      const block = list[index]
      if (!block) return
      // Header/footer are unique layout chrome — never duplicate.
      if (scope === 'layout' && (isLayoutHeader(block.component) || isLayoutFooter(block.component))) {
        return
      }
      const clone: Block = JSON.parse(JSON.stringify(block))
      list.splice(index + 1, 0, clone)
      if (scope === 'layout') {
        this.siteData!.layout.blocks = normalizeLayoutBlockOrder(list)
      }
      this.markDirty()
    },

    addBlock(component: string, scope: 'page' | 'layout' = 'page') {
      if (!this.siteData) return
      const props = buildBlockDefaults(component)
      // Fill locale data on the new block
      for (const loc of this.siteData.i18n.locales) {
        fillLocaleDeep(props, loc)
      }
      const block: Block = { component, enabled: true, props }
      const list = scope === 'layout'
        ? this.siteData.layout.blocks
        : this.siteData.pages[this.activePageSlug]?.blocks
      if (!list) return
      list.push(block)
      if (scope === 'layout') {
        this.siteData.layout.blocks = normalizeLayoutBlockOrder(list)
        this.selectedBlockIndex = this.siteData.layout.blocks.findIndex((b) => b === block)
      } else {
        this.selectedBlockIndex = list.length - 1
      }
      this.selectedScope = scope
      this.sidebarSection = 'blocks'
      // After adding header / footer, sync brand
      if (component === 'BlockHeader') syncHeaderFromGlobal(this.siteData)
      if (component === 'BlockFooter') syncFooterFromGlobal(this.siteData)
      this.markDirty()
    },

    /* ───── Preview navigation ──────────────────── */

    navigateInPreview(to: string) {
      if (!this.siteData) return
      if (!to) return
      if (to.startsWith('http://') || to.startsWith('https://')) {
        if (import.meta.client) window.open(to, '_blank', 'noopener')
        return
      }

      const hashIndex = to.indexOf('#')
      const hash = hashIndex >= 0 ? to.slice(hashIndex + 1) : ''
      const beforeHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to
      // Query (e.g. ?service=… preselect) is meaningless inside the preview.
      const rawPath = beforeHash.split('?')[0] ?? ''

      const scrollPreviewAnchor = (id: string) => {
        if (!import.meta.client || !id) return
        const tryScroll = () => {
          const el = document.getElementById(id)
          if (!el) return false
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          return true
        }
        void nextTick(() => {
          if (tryScroll()) return
          // Page swap may need a frame or two before the wizard mounts.
          requestAnimationFrame(() => {
            if (tryScroll()) return
            window.setTimeout(tryScroll, 80)
          })
        })
      }

      // Same-page hash (e.g. #booking-wizard on the booking page)
      if (!rawPath || to.startsWith('#')) {
        scrollPreviewAnchor(hash || 'booking-wizard')
        return
      }

      const path = (rawPath.startsWith('/') ? rawPath : `/${rawPath}`).replace(/\/$/, '') || '/'
      const entry = Object.entries(this.siteData.pages).find(([key, page]) => {
        const slug = (page.slug || `/${key}`).replace(/\/$/, '') || '/'
        return slug === path || key === path.replace(/^\//, '')
      })
      if (!entry) return
      const [key, page] = entry
      if (page.enabled === false) return
      const alreadyOnPage = this.activePageSlug === key
      this.setActivePageSlug(key)
      // Booking page CTA often looks like the home card — always jump to the wizard.
      if (hash || alreadyOnPage || key === 'booking') {
        scrollPreviewAnchor(hash || 'booking-wizard')
      }
    },

    updateSelectedBlockProp(key: string, value: any) {
      if (!this.siteData) return
      const list = this.selectedScope === 'layout'
        ? this.siteData.layout.blocks
        : this.siteData.pages[this.activePageSlug]?.blocks
      if (!list || this.selectedBlockIndex === null) return
      const block = list[this.selectedBlockIndex]
      if (!block) return
      block.props = { ...block.props, [key]: value }
      this.markDirty()
    },

    getMenuItemsForPreview(): Array<{ label: string; to: string }> {
      if (!this.siteData) return []
      const header = this.siteData.layout.blocks.find(
        (b) => b.component === 'BlockHeader' && b.enabled !== false,
      )
      const raw = (header?.props?.menuItems ?? []) as Array<{
        label?: Translatable | string
        to?: string
      }>
      const locale = this.editLocale
      return raw
        .map((item) => {
          const label =
            typeof item.label === 'string'
              ? item.label
              : (item.label?.[locale] ?? item.label?.[this.defaultLocale] ?? '')
          const to = item.to ?? '#'
          const path = to.startsWith('/') ? to : to.startsWith('#') ? to : `/${to}`
          if (path.startsWith('/')) {
            const pageEntry = Object.entries(this.siteData!.pages).find(
              ([, p]) => p.slug === path,
            )
            if (pageEntry && pageEntry[1].enabled === false) return null
          }
          return { label, to }
        })
        .filter(Boolean) as Array<{ label: string; to: string }>
    },

    /* ───── Languages ───────────────────────────── */

    addLocale(code: Locale) {
      if (!this.siteData) return
      if (this.siteData.i18n.locales.includes(code)) return
      this.siteData.i18n.locales.push(code)

      // Backfill the new locale on every translatable string we know of
      this.siteData.title = ensureLocale(
        this.siteData.title,
        code,
        this.siteData.title[this.siteData.i18n.defaultLocale] ?? '',
      )

      for (const page of Object.values(this.siteData.pages)) {
        page.title = ensureLocale(
          page.title,
          code,
          page.title[this.siteData.i18n.defaultLocale] ?? '',
        )
        page.blocks.forEach((b) => fillLocaleDeep(b.props, code))
      }
      this.siteData.layout.blocks.forEach((b) => fillLocaleDeep(b.props, code))

      this.markDirty()
    },

    removeLocale(code: Locale) {
      if (!this.siteData) return
      if (this.siteData.i18n.locales.length <= 1) return
      this.siteData.i18n.locales = this.siteData.i18n.locales.filter((l) => l !== code)
      if (this.siteData.i18n.defaultLocale === code) {
        this.siteData.i18n.defaultLocale = this.siteData.i18n.locales[0] ?? 'en'
      }
      if (this.editLocale === code) {
        this.editLocale = this.siteData.i18n.defaultLocale
      }

      // Strip translations for that locale (best-effort)
      delete (this.siteData.title as Record<string, string>)[code]
      for (const page of Object.values(this.siteData.pages)) {
        delete (page.title as Record<string, string>)[code]
        page.blocks.forEach((b) => removeLocaleDeep(b.props, code))
      }
      this.siteData.layout.blocks.forEach((b) => removeLocaleDeep(b.props, code))
      this.markDirty()
    },

    setDefaultLocale(code: Locale) {
      if (!this.siteData) return
      if (!this.siteData.i18n.locales.includes(code)) return
      this.siteData.i18n.defaultLocale = code
      this.markDirty()
    },

    /** Dir for editor inputs based on the current edit locale. */
    inputDirForEditLocale(): 'ltr' | 'rtl' {
      return getLocaleInfo(this.editLocale).dir
    },
  },
})
